import React, { useEffect, useState, useMemo } from "react";
import { Select, Table, Input, Switch, InputNumber, Button, DatePicker, Modal, message, Spin, Tooltip } from "antd";
import { CodeOutlined, SearchOutlined, DatabaseOutlined, PlusOutlined, CopyOutlined, DeploymentUnitOutlined, EditOutlined } from "@ant-design/icons";
import dayjs from "dayjs";
import { tenantApi, configApi } from "../../services/api";
import { useEnvironment } from "../../context/EnvironmentContext";
import { useAuth } from "../../context/AuthContext";
import useBreakpoint from "../../hooks/useBreakpoint";
import JsonEditorModal from "./components/JsonEditorModal";
import CreateConfigModal from "./components/CreateConfigModal";
import CopyToEnvsModal from "./components/CopyToEnvsModal";
import CopyToTenantsModal from "./components/CopyToTenantsModal";

function isJsonString(str) {
  if (!str || typeof str !== "string") return false;
  const trimmed = str.trim();
  // Must start with { or [ — plain strings, numbers, booleans are valid JSON but not what we want
  if (!trimmed.startsWith("{") && !trimmed.startsWith("[")) return false;
  try {
    const parsed = JSON.parse(trimmed);
    // Confirm it's actually an object or array, not a primitive
    return typeof parsed === "object" && parsed !== null;
  } catch {
    return false;
  }
}

function getJsonSummary(str) {
  try {
    const parsed = JSON.parse(str);
    if (Array.isArray(parsed)) return `Array [${parsed.length} items]`;
    const keys = Object.keys(parsed);
    if (keys.length <= 3) return keys.join(", ");
    return `${keys.slice(0, 3).join(", ")} +${keys.length - 3} more`;
  } catch { return str; }
}

function getValueType(value) {
  if (!value) return "text";
  if (isJsonString(value)) return "json";
  if (value === "true" || value === "false") return "toggle";
  if (dayjs(value, ["YYYY-MM-DD", "DD/MM/YYYY", "MM/DD/YYYY"], true).isValid()) return "date";
  if (!isNaN(value) && value.trim() !== "") return "number";
  return "text";
}

// color/bg feed the desktop chip bar via --chip-color/--chip-bg;
// className styles the mobile card badge (same palette, Tailwind names).
const TYPE_CHIPS = [
  { key: "all",    label: "All",    color: "#64748b", bg: "#f1f5f9", className: "text-slate-500 bg-slate-100" },
  { key: "json",   label: "JSON",   color: "#7c3aed", bg: "#f5f3ff", className: "text-violet-600 bg-violet-50" },
  { key: "toggle", label: "Toggle", color: "#059669", bg: "#ecfdf5", className: "text-emerald-600 bg-emerald-50" },
  { key: "date",   label: "Date",   color: "#0369a1", bg: "#e0f2fe", className: "text-sky-700 bg-sky-100" },
  { key: "number", label: "Number", color: "#d97706", bg: "#fffbeb", className: "text-amber-600 bg-amber-50" },
  { key: "text",   label: "Text",   color: "#475569", bg: "#f8fafc", className: "text-slate-600 bg-slate-50" },
];

export default function ConfigEditor() {
  const { selectedEnv } = useEnvironment();
  const { hasPermission } = useAuth();
  const { isMobile } = useBreakpoint();
  const [tenants, setTenants] = useState([]);
  const [tenantsLoading, setTenantsLoading] = useState(false);
  const [selectedTenant, setSelectedTenant] = useState(null);
  const [configData, setConfigData] = useState([]);
  const [tabKey, setTabKey] = useState("alias");
  const [searchText, setSearchText] = useState("");
  const [typeFilter, setTypeFilter] = useState("all");
  const [editedRows, setEditedRows] = useState({});
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState({});
  const [jsonModal, setJsonModal] = useState({ visible: false, recordId: null, field: null, value: "", title: "" });
  const [createModal, setCreateModal] = useState(false);
  const [copyModal, setCopyModal]         = useState({ visible: false, record: null });
  const [copyTenantsModal, setCopyTenantsModal] = useState({ visible: false, record: null });
  // Mobile-only: which row is being edited in the per-row modal.
  const [editingId, setEditingId] = useState(null);

  // Permission flags for current env + tenant
  const canEdit = useMemo(() => hasPermission(selectedEnv, selectedTenant, "config:edit"), [selectedEnv, selectedTenant, hasPermission]);
  const canCreate = useMemo(() => hasPermission(selectedEnv, selectedTenant, "config:create"), [selectedEnv, selectedTenant, hasPermission]);
  const canCopy = useMemo(() => hasPermission(selectedEnv, selectedTenant, "config:copy"), [selectedEnv, selectedTenant, hasPermission]);
  const isReadOnly = !canEdit;

  const byName = (a, b) => (a.name || "").toLowerCase().localeCompare((b.name || "").toLowerCase());
  const aliasData = configData.filter((item) => item.config_type === "alias").sort(byName);
  const configurationData = configData.filter((item) => item.config_type === "config").sort(byName);

  useEffect(() => {
    setSelectedTenant(null);
    setConfigData([]);
    setSearchText("");
    setTypeFilter("all");
    setEditedRows({});
    // Clear the previous env's tenants before fetching so the dropdown
    // doesn't keep showing stale options (or sticky options forever if the
    // new fetch errors).
    setTenants([]);
    setTenantsLoading(true);
    let cancelled = false;
    const fetchTenants = async () => {
      try {
        const res = await tenantApi.getAll();
        if (!cancelled) setTenants(res.data);
      } catch (err) {
        if (cancelled) return;
        const data = err.response?.data || {};
        const detail = data.detail || data.message || data.error;
        message.error(detail ? `Failed to fetch tenants: ${detail}` : "Failed to fetch tenants", 10);
      } finally {
        if (!cancelled) setTenantsLoading(false);
      }
    };
    fetchTenants();
    return () => { cancelled = true; };
  }, [selectedEnv]);

  const handleTenantChange = async (schemaName) => {
    setSelectedTenant(schemaName);
    setSearchText("");
    setTypeFilter("all");
    setEditedRows({});
    setLoading(true);
    try {
      const res = await configApi.getConfig(schemaName);
      setConfigData(res.data);
    } catch { message.error("Failed to fetch configuration"); }
    finally { setLoading(false); }
  };

  const handleCellChange = (id, key, value) => {
    setConfigData((prev) => prev.map((row) => (row.id === id ? { ...row, [key]: value } : row)));
    setEditedRows((prev) => ({ ...prev, [id]: { ...prev[id], [key]: value } }));
  };

  const handleSave = async (record, { closeOnSuccess = false } = {}) => {
    const changes = editedRows[record.id];
    if (!changes) return;
    setSaving((prev) => ({ ...prev, [record.id]: true }));
    try {
      await configApi.updateConfig(selectedTenant, record.id, changes);
      message.success(`Updated "${record.name}" successfully`);
      setEditedRows((prev) => { const copy = { ...prev }; delete copy[record.id]; return copy; });
      if (closeOnSuccess) setEditingId(null);
    } catch (err) {
      message.error(err.response?.data?.error || "Failed to save");
    } finally { setSaving((prev) => ({ ...prev, [record.id]: false })); }
  };

  const handleCreated = (newRow) => {
    setConfigData((prev) => [...prev, newRow]);
  };

  const handleJsonSave = (newValue) => {
    handleCellChange(jsonModal.recordId, jsonModal.field, newValue);
    setJsonModal({ visible: false, recordId: null, field: null, value: "", title: "" });
  };

  const openJsonModal = (record, field) => {
    setJsonModal({ visible: true, recordId: record.id, field, value: record[field] || "", title: record.name });
  };

  // ── Alias cell ──────────────────────────────────────────────────────────────
  const renderAliasCell = (_, record) => {
    if (!record.alias_name) return <span className="text-slate-400">--</span>;
    if (isReadOnly) return <span>{record.alias_name}</span>;
    return (
      <Input
        value={record.alias_name}
        onChange={(e) => handleCellChange(record.id, "alias_name", e.target.value)}
        className="w-full"
      />
    );
  };

  // ── Config value cell ───────────────────────────────────────────────────────
  const renderValueCell = (_, record) => {
    if (!record.value) return <span className="text-slate-400">--</span>;

    const type = getValueType(record.value);

    if (type === "json") {
      if (isReadOnly) {
        let prettyJson;
        try { prettyJson = JSON.stringify(JSON.parse(record.value), null, 2); } catch { prettyJson = record.value; }
        return (
          <pre className="m-0 text-[11px] text-slate-700 bg-slate-50 rounded py-1 px-2 max-h-[120px] overflow-auto whitespace-pre-wrap [word-break:break-word]">
            {prettyJson}
          </pre>
        );
      }
      return (
        <div className="json-inline">
          <span className="json-code">{getJsonSummary(record.value)}</span>
          <Button type="text" size="small" className="json-expand-btn" icon={<CodeOutlined />}
            onClick={() => openJsonModal(record, "value")}>
            Edit JSON
          </Button>
        </div>
      );
    }

    if (type === "toggle") {
      return (
        <Switch
          checked={record.value === "true"}
          onChange={(checked) => handleCellChange(record.id, "value", checked ? "true" : "false")}
          disabled={isReadOnly}
        />
      );
    }

    if (isReadOnly) return <span>{record.value}</span>;

    if (type === "date") {
      return (
        <DatePicker
          value={dayjs(record.value)}
          allowClear={false}
          format="YYYY-MM-DD"
          onChange={(_, dateString) => handleCellChange(record.id, "value", dateString)}
          className="w-full"
        />
      );
    }

    if (type === "number") {
      return (
        <InputNumber
          value={Number(record.value)}
          onChange={(value) => handleCellChange(record.id, "value", String(value))}
          className="!w-full"
        />
      );
    }

    return (
      <Input.TextArea
        value={record.value}
        autoSize={{ minRows: 1, maxRows: 3 }}
        onChange={(e) => handleCellChange(record.id, "value", e.target.value)}
        className="w-full"
      />
    );
  };

  // ── Columns ─────────────────────────────────────────────────────────────────
  const columns = [
    ...(canCopy ? [{
      title: "",
      width: 72,
      align: "center",
      render: (_, record) => (
        <div className="flex gap-0.5 justify-center">
          <Tooltip title="Copy to other tenants (same env)">
            <Button
              type="text"
              size="small"
              icon={<DeploymentUnitOutlined />}
              onClick={() => setCopyTenantsModal({ visible: true, record })}
              className="!text-slate-400"
            />
          </Tooltip>
          <Tooltip title="Copy to other environments">
            <Button
              type="text"
              size="small"
              icon={<CopyOutlined />}
              onClick={() => setCopyModal({ visible: true, record })}
              className="!text-slate-400"
            />
          </Tooltip>
        </div>
      ),
    }] : []),
    {
      title: "Name",
      dataIndex: "name",
      width: 220,
      render: (text) => <span className="font-medium">{text}</span>,
    },
    ...(tabKey === "alias"
      ? [{ title: "Alias Value", dataIndex: "alias_name", render: renderAliasCell }]
      : [{ title: "Value", dataIndex: "value", render: renderValueCell }]),
    {
      title: "Status",
      dataIndex: "is_active",
      width: 80,
      align: "center",
      render: (_, record) => (
        <Switch
          checked={Boolean(record.is_active)}
          onChange={(checked) => handleCellChange(record.id, "is_active", checked)}
          size="small"
          disabled={isReadOnly}
        />
      ),
    },
    ...(canEdit ? [{
      title: "",
      width: 90,
      align: "center",
      render: (_, record) => (
        <Button
          type="primary"
          size="small"
          className="save-btn"
          onClick={() => handleSave(record)}
          disabled={!editedRows[record.id]}
          loading={saving[record.id]}
        >
          Save
        </Button>
      ),
    }] : []),
  ];

  // ── Filtering ────────────────────────────────────────────────────────────────
  const rawData = tabKey === "alias" ? aliasData : configurationData;

  // Alias: search on name OR alias_name
  // Config: search on name + type chip filter
  const currentData = rawData.filter((item) => {
    const q = searchText.toLowerCase().trim();

    if (tabKey === "alias") {
      const matchName  = (item.name || "").toLowerCase().includes(q);
      const matchAlias = (item.alias_name || "").toLowerCase().includes(q);
      return !q || matchName || matchAlias;
    }

    // Config tab — name search
    if (q && !(item.name || "").toLowerCase().includes(q)) return false;

    // Config tab — type chip filter
    if (typeFilter !== "all" && getValueType(item.value) !== typeFilter) return false;

    return true;
  });

  // Count per type for chip badges
  const typeCounts = TYPE_CHIPS.reduce((acc, chip) => {
    if (chip.key === "all") {
      acc[chip.key] = configurationData.length;
    } else {
      acc[chip.key] = configurationData.filter((item) => getValueType(item.value) === chip.key).length;
    }
    return acc;
  }, {});

  return (
    <div>
      <div className="page-header">
        <h1 className="page-title">Configuration</h1>
        <p className="page-subtitle">Manage tenant app configurations and aliases</p>
      </div>

      {/* ── Toolbar ── */}
      <div className="config-toolbar">
        <Select
          value={selectedTenant}
          placeholder={tenantsLoading ? "Loading tenants..." : "Select a tenant..."}
          className="w-[280px]"
          options={tenants.map((t) => ({ label: t.subdomain, value: t.name }))}
          onChange={handleTenantChange}
          loading={tenantsLoading}
          disabled={tenantsLoading}
          showSearch
          suffixIcon={<SearchOutlined className="text-slate-400" />}
          filterOption={(input, option) =>
            option.label.toLowerCase().includes(input.toLowerCase())
          }
          size="large"
        />

        {selectedTenant && (
          <>
            <div className="config-tab-bar">
              <button
                className={`config-tab${tabKey === "alias" ? " active" : ""}`}
                onClick={() => { setTabKey("alias"); setSearchText(""); setTypeFilter("all"); }}
                disabled={loading}
              >
                Aliases
              </button>
              <button
                className={`config-tab${tabKey === "config" ? " active" : ""}`}
                onClick={() => { setTabKey("config"); setSearchText(""); setTypeFilter("all"); }}
                disabled={loading}
              >
                Config
              </button>
            </div>

            <Input
              placeholder={tabKey === "alias" ? "Search name or alias..." : "Search by name..."}
              prefix={<SearchOutlined className="text-slate-400" />}
              value={searchText}
              onChange={(e) => setSearchText(e.target.value)}
              allowClear
              className="!w-[260px]"
            />

            {canCreate && (
              <Button
                type="primary"
                icon={<PlusOutlined />}
                onClick={() => setCreateModal(true)}
                className="ml-auto"
              >
                Add {tabKey === "alias" ? "Alias" : "Config"}
              </Button>
            )}
          </>
        )}
      </div>

      {/* ── Type chip filters (config tab only) ── */}
      {selectedTenant && tabKey === "config" && !loading && (
        <div className="type-chip-bar">
          {TYPE_CHIPS.filter((c) => c.key === "all" || typeCounts[c.key] > 0).map((chip) => {
            const active = typeFilter === chip.key;
            return (
              <button
                key={chip.key}
                className={`type-chip${active ? " active" : ""}`}
                style={{
                  "--chip-color": chip.color,
                  "--chip-bg": chip.bg,
                }}
                onClick={() => setTypeFilter(chip.key)}
              >
                {chip.label}
                <span className="type-chip-count">{typeCounts[chip.key]}</span>
              </button>
            );
          })}
        </div>
      )}

      {/* ── Table (desktop) / Card list (mobile) ── */}
      {selectedTenant ? (
        <Spin spinning={loading}>
          {!isMobile ? (
            <div className="sq-table">
              <Table
                rowKey="id"
                dataSource={currentData}
                columns={columns}
                size="middle"
                scroll={{ x: true }}
                pagination={{
                  defaultPageSize: 15,
                  pageSizeOptions: [10, 15, 25, 50, 100],
                  showSizeChanger: true,
                  showTotal: (total) => (
                    <span className="text-slate-500 text-[13px]">{total} items</span>
                  ),
                  size: "small",
                }}
              />
            </div>
          ) : (
            <div className="card-list">
              {currentData.length === 0 && (
                <div className="empty-state py-6 px-3">
                  <div className="empty-state-text">No {tabKey === "alias" ? "aliases" : "configs"} found</div>
                </div>
              )}
              {currentData.map((record) => {
                const preview = tabKey === "alias"
                  ? (record.alias_name || "")
                  : (record.value || "");
                const type = tabKey === "config" ? getValueType(record.value) : null;
                const typeChip = type && TYPE_CHIPS.find((c) => c.key === type);
                return (
                  <div key={record.id} className="card-list-item">
                    <div className="card-list-item-title [word-break:break-word]">
                      {record.name}
                    </div>
                    <div className="card-list-item-meta">
                      {typeChip && (
                        <span className={`text-[11px] font-medium py-0.5 px-2 rounded-[10px] ${typeChip.className}`}>
                          {typeChip.label}
                        </span>
                      )}
                      <span className={`text-[11px] font-medium py-0.5 px-2 rounded-[10px] ${record.is_active ? "text-emerald-600 bg-emerald-50" : "text-slate-400 bg-slate-100"}`}>
                        {record.is_active ? "Active" : "Inactive"}
                      </span>
                    </div>
                    <div className="card-list-item-sub code-box">
                      {preview || <span className="text-slate-400">empty</span>}
                    </div>
                    <div className="card-list-item-actions">
                      <Button
                        size="small"
                        icon={<EditOutlined />}
                        onClick={() => setEditingId(record.id)}
                        disabled={isReadOnly && !canCopy}
                        type={canEdit ? "primary" : "default"}
                      >
                        {canEdit ? "Edit" : "View"}
                      </Button>
                      {canCopy && (
                        <Button size="small" icon={<DeploymentUnitOutlined />} onClick={() => setCopyTenantsModal({ visible: true, record })}>
                          Tenants
                        </Button>
                      )}
                      {canCopy && (
                        <Button size="small" icon={<CopyOutlined />} onClick={() => setCopyModal({ visible: true, record })}>
                          Envs
                        </Button>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </Spin>
      ) : (
        <div className="empty-state">
          <DatabaseOutlined className="empty-state-icon" />
          <div className="empty-state-text">Select a tenant to get started</div>
          <div className="empty-state-subtext">Choose a tenant from the dropdown above to view and edit configurations</div>
        </div>
      )}

      {/* Mobile-only per-row edit modal — reuses the same renderers as the
         desktop table cells, so JSON / toggle / date editors all work
         identically. */}
      {(() => {
        const editingRecord = editingId != null ? configData.find((r) => r.id === editingId) : null;
        if (!editingRecord) return null;
        return (
          <Modal
            title={editingRecord.name}
            open
            onCancel={() => setEditingId(null)}
            destroyOnClose
            footer={[
              <Button key="close" onClick={() => setEditingId(null)}>Close</Button>,
              canEdit && (
                <Button
                  key="save"
                  type="primary"
                  loading={saving[editingRecord.id]}
                  disabled={!editedRows[editingRecord.id]}
                  onClick={() => handleSave(editingRecord, { closeOnSuccess: true })}
                >
                  Save
                </Button>
              ),
            ].filter(Boolean)}
          >
            <div className="flex flex-col gap-3">
              <div>
                <div className="mini-label mb-1.5">
                  {tabKey === "alias" ? "Alias value" : "Value"}
                </div>
                {tabKey === "alias"
                  ? renderAliasCell(null, editingRecord)
                  : renderValueCell(null, editingRecord)}
              </div>
              <div className="flex items-center justify-between">
                <span className="text-[13px] font-medium text-slate-600">Active</span>
                <Switch
                  checked={Boolean(editingRecord.is_active)}
                  onChange={(checked) => handleCellChange(editingRecord.id, "is_active", checked)}
                  disabled={isReadOnly}
                />
              </div>
            </div>
          </Modal>
        );
      })()}

      <JsonEditorModal
        visible={jsonModal.visible}
        value={jsonModal.value}
        title={jsonModal.title}
        onSave={handleJsonSave}
        onCancel={() => setJsonModal({ visible: false, recordId: null, field: null, value: "", title: "" })}
      />

      <CreateConfigModal
        visible={createModal}
        onCancel={() => setCreateModal(false)}
        onCreated={handleCreated}
        configType={tabKey === "alias" ? "alias" : "config"}
        tenantSchema={selectedTenant}
      />

      <CopyToEnvsModal
        visible={copyModal.visible}
        onCancel={() => setCopyModal({ visible: false, record: null })}
        record={copyModal.record}
        configType={copyModal.record?.config_type || tabKey}
        currentEnv={selectedEnv}
        tenantSchema={selectedTenant}
      />

      <CopyToTenantsModal
        visible={copyTenantsModal.visible}
        onCancel={() => setCopyTenantsModal({ visible: false, record: null })}
        record={copyTenantsModal.record}
        configType={copyTenantsModal.record?.config_type || tabKey}
        currentEnv={selectedEnv}
        tenantSchema={selectedTenant}
      />
    </div>
  );
}
